import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { userGoalService } from "@/services/userGoalService";
import { activityService } from "@/services/activityService";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Award, 
  BookOpen, 
  CheckCircle2, 
  ChevronRight, 
  ChevronLeft,
  Compass, 
  Target, 
  ArrowRight, 
  Loader2, 
  Calendar, 
  Sparkles, 
  Check, 
  HelpCircle,
  FileText,
  Volume2,
  Bookmark,
  AlertCircle
} from "lucide-react";

export default function Onboarding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [targetNclc, setTargetNclc] = useState<number>(7);
  const [examDate, setExamDate] = useState<string>("");
  const [mainFocus, setMainFocus] = useState<string>("general");
  const [loading, setLoading] = useState(false);

  // Track when onboarding starts
  useEffect(() => {
    activityService.trackUserActivity("onboarding_started", "profile", null, {
      timestamp: new Date().toISOString()
    });
  }, []);

  const handleComplete = async (redirectPath: string) => {
    setLoading(true);
    try {
      await userGoalService.completeOnboarding({
        mainFocus,
        targetNclc,
        examDate: examDate || null
      });
      
      await activityService.trackUserActivity("onboarding_completed", "profile", null, {
        mainFocus,
        targetNclc,
        examDate: examDate || null,
        redirectPath
      });
      
      navigate(redirectPath);
    } catch (err) {
      console.error("Error completing onboarding:", err);
      navigate(redirectPath);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    try {
      await userGoalService.completeOnboarding({
        mainFocus: "general",
        targetNclc: 7,
        examDate: null
      });
      
      await activityService.trackUserActivity("onboarding_skipped", "profile", null, {
        timestamp: new Date().toISOString()
      });
      
      navigate("/dashboard");
    } catch {
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <div className="text-center space-y-3">
              <div className="mx-auto w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-2 shadow-inner">
                <Compass className="w-7 h-7 animate-pulse" />
              </div>
              <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight sm:text-3xl">
                {t("onboarding.welcome")}
              </h2>
              <p className="text-sm text-gray-500 leading-relaxed max-w-md mx-auto">
                {t("onboarding.welcome_desc")}
              </p>
            </div>

            <div className="bg-gray-50/50 backdrop-blur-xs p-5 rounded-2xl border border-gray-100 space-y-4">
              <h3 className="text-xs font-extrabold uppercase tracking-wider text-primary flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" />
                {t("onboarding.steps_overview_title")}
              </h3>
              <div className="grid gap-3 text-left">
                {[
                  { key: "step_goal", desc: t("onboarding.step_goal") },
                  { key: "step_test", desc: t("onboarding.step_test") },
                  { key: "step_errors", desc: t("onboarding.step_errors") },
                  { key: "step_flashcards", desc: t("onboarding.step_flashcards") },
                  { key: "step_plan", desc: t("onboarding.step_plan") }
                ].map((item) => (
                  <div key={item.key} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5 text-[10px] font-bold">
                      ✓
                    </div>
                    <span className="text-xs text-gray-600 font-medium leading-normal">
                      {item.desc}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-4">
              <Button 
                onClick={() => setStep(2)} 
                className="w-full sm:w-auto px-8 py-6 rounded-2xl font-bold text-sm shadow-md bg-primary hover:bg-primary/95 text-white transition-all transform hover:scale-[1.02]"
              >
                <span>{t("common.continue", "Continue")}</span>
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="text-center space-y-1">
              <div className="mx-auto w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-2 shadow-inner">
                <Target className="w-6 h-6" />
              </div>
              <h2 className="text-xl font-extrabold text-gray-900 tracking-tight">
                {t("onboarding.goals_title")}
              </h2>
              <p className="text-xs text-gray-500 max-w-sm mx-auto">
                {t("onboarding.goals_desc")}
              </p>
            </div>

            <div className="space-y-5 text-left pt-2">
              {/* NCLC Level Selector */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-700 tracking-wide uppercase block">
                  {t("onboarding.target_nclc")}
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[7, 8, 9, 10].map((level) => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setTargetNclc(level)}
                      className={`py-3.5 rounded-xl border-2 font-bold text-sm transition-all flex flex-col items-center justify-center ${
                        targetNclc === level 
                          ? "border-primary bg-primary/5 text-primary shadow-xs" 
                          : "border-gray-100 bg-white hover:border-gray-200 text-gray-600"
                      }`}
                    >
                      <span className="text-base font-extrabold">NCLC {level}</span>
                      <span className="text-[9px] font-medium opacity-80">
                        {level === 7 ? "B2" : level === 8 ? "C1" : level === 9 ? "C1+" : "C2"}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Exam Date Picker */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-700 tracking-wide uppercase flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-gray-400" />
                  {t("onboarding.exam_date")}
                </label>
                <input
                  type="date"
                  value={examDate}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setExamDate(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-hidden focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all bg-white font-medium"
                />
              </div>

              {/* Main Focus competency selection */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-700 tracking-wide uppercase block">
                  {t("onboarding.main_focus")}
                </label>
                <div className="grid gap-2 sm:grid-cols-2">
                  {[
                    { id: "general", label: t("onboarding.focus_all"), icon: Sparkles },
                    { id: "ce", label: t("onboarding.focus_reading"), icon: FileText },
                    { id: "co", label: t("onboarding.focus_listening"), icon: Volume2 },
                    { id: "ee", label: t("onboarding.focus_writing"), icon: BookOpen }
                  ].map((option) => {
                    const Icon = option.icon;
                    const isSelected = mainFocus === option.id;
                    return (
                      <div
                        key={option.id}
                        onClick={() => setMainFocus(option.id)}
                        className={`p-3.5 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-3 ${
                          isSelected 
                            ? "border-primary bg-primary/5 shadow-xs" 
                            : "border-gray-100 hover:border-gray-200 bg-white"
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                          isSelected ? "bg-primary/20 text-primary" : "bg-gray-50 text-gray-400"
                        }`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="text-left">
                          <span className="font-bold text-xs text-gray-900 block leading-tight">
                            {option.label}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="pt-4 flex justify-between gap-3">
              <Button 
                variant="outline" 
                onClick={() => setStep(1)} 
                className="rounded-xl text-xs font-bold px-5 border-gray-200 py-5 flex items-center gap-1.5"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                {t("common.back", "Back")}
              </Button>
              <Button 
                onClick={() => setStep(3)} 
                className="rounded-xl text-xs font-bold px-8 bg-primary hover:bg-primary/95 text-white py-5 flex items-center gap-1"
              >
                <span>{t("common.continue", "Continue")}</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="text-center space-y-1">
              <div className="mx-auto w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-500 mb-2 shadow-inner">
                <AlertCircle className="w-6 h-6 animate-pulse" />
              </div>
              <h2 className="text-xl font-extrabold text-gray-900 tracking-tight">
                {t("onboarding.trial_expl_title")}
              </h2>
              <p className="text-xs text-gray-500 max-w-sm mx-auto">
                {t("onboarding.trial_expl_desc")}
              </p>
            </div>

            <div className="grid gap-3 pt-2 text-left sm:grid-cols-2">
              {[
                { 
                  label: "Reading & Listening", 
                  limit: "4 " + t("common.tests", "Tests"),
                  desc: t("onboarding.trial_item_tests"),
                  icon: FileText
                },
                { 
                  label: "AI Writing Corrections", 
                  limit: "3 " + t("common.corrections", "Corrections"),
                  desc: t("onboarding.trial_item_corrections"),
                  icon: Sparkles
                },
                { 
                  label: "Active Recall Flashcards", 
                  limit: "50 " + t("common.flashcards", "Cards"),
                  desc: t("onboarding.trial_item_flashcards"),
                  icon: Bookmark
                },
                { 
                  label: "Platform Error Bank", 
                  limit: "50 " + t("common.errors", "Errors"),
                  desc: t("onboarding.trial_item_errors"),
                  icon: Award
                }
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div key={idx} className="p-4 rounded-2xl border border-gray-100 bg-gray-50/40 backdrop-blur-xs flex flex-col justify-between space-y-2 shadow-2xs">
                    <div className="flex items-center justify-between">
                      <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                        <Icon className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-[10px] font-extrabold px-2.5 py-0.5 rounded-full bg-primary/10 text-primary uppercase tracking-wider">
                        {item.limit}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-extrabold text-xs text-gray-900 block mb-0.5">{item.label}</h4>
                      <p className="text-[11px] text-gray-500 leading-normal">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="pt-4 flex justify-between gap-3">
              <Button 
                variant="outline" 
                onClick={() => setStep(2)} 
                className="rounded-xl text-xs font-bold px-5 border-gray-200 py-5 flex items-center gap-1.5"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                {t("common.back", "Back")}
              </Button>
              <Button 
                onClick={() => setStep(4)} 
                className="rounded-xl text-xs font-bold px-8 bg-primary hover:bg-primary/95 text-white py-5 flex items-center gap-1"
              >
                <span>{t("common.continue", "Continue")}</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="text-center space-y-1">
              <div className="mx-auto w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600 mb-2 shadow-inner">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h2 className="text-xl font-extrabold text-gray-900 tracking-tight">
                {t("onboarding.step4_title")}
              </h2>
              <p className="text-xs text-gray-500 max-w-sm mx-auto">
                {t("onboarding.welcome_desc")}
              </p>
            </div>

            <div className="bg-primary/5 p-5 rounded-2xl border border-primary/10 text-center space-y-2 shadow-xs">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-primary block">
                {t("common.recommendation", "Recommendation")}
              </span>
              <p className="text-xs font-bold text-gray-800 max-w-xs mx-auto leading-relaxed">
                {mainFocus === "general" && t("onboarding.general_rec", "We recommend starting with a mock Reading or Listening diagnostic test.")}
                {mainFocus === "ce" && t("onboarding.ce_rec", "Start with a Reading comprehension test to benchmark your level.")}
                {mainFocus === "co" && t("onboarding.co_rec", "Start with a Listening comprehension test to benchmark your level.")}
                {mainFocus === "ee" && t("onboarding.ee_rec", "Write your first essay simulation to receive detailed AI corrections.")}
              </p>
            </div>

            <div className="grid gap-3 pt-2">
              <div className="flex flex-col sm:flex-row gap-2">
                <Button 
                  disabled={loading}
                  onClick={() => handleComplete("/start-test?competence=reading")} 
                  className="flex-1 py-5 rounded-xl font-bold text-xs bg-primary hover:bg-primary/95 text-white shadow-xs flex items-center justify-center gap-1.5"
                >
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                  <span>{t("onboarding.start_reading")}</span>
                </Button>

                <Button 
                  disabled={loading}
                  onClick={() => handleComplete("/start-test?competence=listening")} 
                  className="flex-1 py-5 rounded-xl font-bold text-xs bg-primary hover:bg-primary/95 text-white shadow-xs flex items-center justify-center gap-1.5"
                >
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />}
                  <span>{t("onboarding.start_listening")}</span>
                </Button>
              </div>

              <Button 
                variant="outline"
                disabled={loading}
                onClick={() => handleComplete("/dashboard")} 
                className="w-full py-5 rounded-xl font-bold text-xs border-gray-200 flex items-center justify-center gap-1.5 text-gray-700 hover:bg-gray-50"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                <span>{t("onboarding.go_to_dashboard")}</span>
                <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
              </Button>
            </div>

            <div className="text-center pt-2">
              <Button 
                variant="ghost" 
                size="sm"
                disabled={loading}
                onClick={handleSkip} 
                className="text-xs text-gray-400 hover:text-gray-600 hover:bg-transparent"
              >
                {t("onboarding.skip_onboarding")}
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F8F9FA] p-4 font-sans select-none antialiased">
      <Card className="w-full max-w-lg border-none shadow-2xl bg-white rounded-3xl overflow-hidden transition-all duration-300">
        <CardHeader className="bg-primary/5 pb-5 pt-7 text-center border-b border-primary/10 relative">
          <CardTitle className="text-base font-extrabold text-primary uppercase tracking-wider flex items-center justify-center gap-1">
            <Sparkles className="w-4 h-4 text-primary animate-pulse" />
            <span>TEF Canada Trainer</span>
          </CardTitle>
          <CardDescription className="text-[11px] text-gray-500 mt-1 font-semibold">
            {t("onboarding.step_title", { current: step, total: 4 })}
          </CardDescription>
          
          <div className="flex justify-center gap-1.5 mt-3.5">
            {[1, 2, 3, 4].map(s => (
              <div 
                key={s} 
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  s === step ? "w-8 bg-primary" : s < step ? "w-2.5 bg-primary/40" : "w-2.5 bg-gray-200"
                }`} 
              />
            ))}
          </div>
        </CardHeader>
        <CardContent className="p-6 sm:p-8">
          {renderStepContent()}
        </CardContent>
      </Card>
    </div>
  );
}
